import React, { useCallback, useEffect, useRef, useState } from "react";
import { Flex, Box } from "@chakra-ui/react";
import { useInstantSearch } from "react-instantsearch";
import { AlgoliaSearchInput, useAlgoliaFilterContext } from "components/algolia";
import { useHeaderHeight } from "contexts/header-height";
import { Chip } from "ui/chips";
import { Section, sectionSpacingY } from "components/section";
import IntegrationsSearchResults from "./integrations-search-results";
import IntegrationsSidebarFilters, { categoryAttribute, typeAttribute } from "./integrations-sidebar-filters";

function IntegrationsCatalog() {
  const catalogRef = useRef<HTMLDivElement>(null);
  const { headerHeight } = useHeaderHeight();
  const { results, status } = useInstantSearch();
  const { currentFilter: selectedType, clearFilter: clearType } = useAlgoliaFilterContext(typeAttribute);
  const { currentFilter: selectedCategory, clearFilter: clearCategory } = useAlgoliaFilterContext(categoryAttribute);
  const hasFilters = Boolean(selectedType ?? selectedCategory);

  const [isLoading, setIsLoading] = useState(true);
  const [isInitialRender, setIsInitialRender] = useState(true);

  const mainContentPt = "smaller";

  // This will allow us to show a loading state on initial render, since the Algolia results are actually "idle" from already being statically generated.
  // Also used to infer a proper loading state when filters are changed, since using the actual "loading" status from Algolia is not recommended as best practice.
  // https://www.algolia.com/doc/guides/building-search-ui/ui-and-ux-patterns/loading-indicator/js/
  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
    } else if (isLoading && status === "idle") {
      // By waiting for initialRender to be false before setting isLoading to false, we ensure that
      // a page with URL search parameters included will have had time to fetch the filtered results
      setIsLoading(false);
    } else if (status === "stalled") {
      setIsLoading(true);
    }
  }, [status, isInitialRender, isLoading]);

  const onFilterChanged = useCallback(() => {
    setIsLoading(true);
  }, []);

  useEffect(() => {
    // Scroll catalog to top when search begins or filter clicked
    if (
      catalogRef?.current &&
      window.scrollY !== catalogRef.current.offsetTop &&
      results?.query.length < 2 &&
      window.scrollY > 0 // Don't scroll on page load
    ) {
      window.scrollTo({ top: catalogRef.current.offsetTop - headerHeight, behavior: "smooth" });
    }
  }, [selectedType, selectedCategory, results?.query.length, headerHeight]);

  return (
    <Section my={0} pb={sectionSpacingY} minH={results?.nbHits > 12 ? "calc(100vh - 81px)" : "auto"}>
      <Flex
        ref={catalogRef}
        direction={["column", "column", "column", "row"]}
        align="start"
        justify="space-between"
        h="100%"
        minH={0}
      >
        <IntegrationsSidebarFilters
          query={results?.query ?? ""}
          pt={mainContentPt}
          headerHeight={`${headerHeight}px`}
          onFilterChanged={onFilterChanged}
        />

        <Box w={["100%", "100%", "100%", "75%"]}>
          {/* only sticky if page is scrollable */}
          <Box
            position={{
              base: results?.nbHits > 3 ? "sticky" : "relative",
              lg: results?.nbHits > 6 ? "sticky" : "relative",
            }}
            w="100%"
            top={results?.nbHits > 6 ? `${headerHeight}px` : 0}
            zIndex={300}
          >
            {/* negative x-margins to hide card shadows on scroll */}
            <Box
              bg="white"
              w={`calc(100% + ${16})`}
              pt={mainContentPt}
              mx={{ base: -4, xl: -8 }}
              px={{ base: 4, xl: 8 }}
            >
              <Box w="100%">
                <AlgoliaSearchInput placeholder="Search for an integration..." />

                <Flex gap={3} pt={hasFilters ? 6 : 0}>
                  {selectedType && (
                    <Chip
                      variant="gray"
                      onCloseClick={async () => {
                        await clearType();
                        onFilterChanged();
                      }}
                    >
                      <Box as="span" textTransform="capitalize">
                        {selectedType}
                      </Box>
                    </Chip>
                  )}

                  {selectedCategory && (
                    <Chip
                      variant="gray"
                      onCloseClick={async () => {
                        await clearCategory();
                        onFilterChanged();
                      }}
                    >
                      {selectedCategory}
                    </Chip>
                  )}
                </Flex>
              </Box>
            </Box>

            <Box
              mx={{ base: 0, xl: -8 }}
              w={`calc(100% + ${16})`}
              h="smaller"
              bgGradient="linear(#fff, 90%, rgba(255, 255, 255, 0))"
            />
          </Box>

          <IntegrationsSearchResults isLoading={isLoading} />
        </Box>
      </Flex>
    </Section>
  );
}

export default IntegrationsCatalog;
